import { useState, useEffect } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { SimpleErrorBoundary, NotFound, LambLoader, LoginFailedScreen } from '../shared/components';
import { AuthCallback, LogoutSuccess } from '../features/auth';
import { Paywall } from '../features/paywall';
import { DashboardRedirect } from './HomeRedirect';
import { RoleProtectedRoute } from './RoleProtectedRoute';
import { useAuth } from './providers/AuthContext';
import { useSignInWithTimeout } from './hooks/useSignInWithTimeout';
import { preloadCourseConfig } from '../data/lessonsRegistry';
import { getQRCourseName } from '../utils/routeUtils';
import { lazyWithRetry } from '../utils/lazyWithRetry';

/**
 * OptimisticProtectedRoute - Renders children immediately while auth loads in background.
 * Auth redirect happens once auth check completes. Used for faster FCP.
 */
function OptimisticProtectedRoute({ children }) {
  const { isSignedIn, loading } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { loginFailed, triggerSignIn, reset } = useSignInWithTimeout();
  const location = useLocation();

  // Preload course config early for QR routes
  useEffect(() => {
    const courseName = getQRCourseName(location.pathname);
    if (courseName) {
      preloadCourseConfig(courseName);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && !isSignedIn) {
      const justLoggedOut = sessionStorage.getItem('just_logged_out');
      if (justLoggedOut) return;
      setShouldRedirect(true);
      triggerSignIn();
    }
  }, [loading, isSignedIn, triggerSignIn]);

  if (loginFailed) {
    return <LoginFailedScreen onRetry={() => { reset(); setShouldRedirect(false); }} />;
  }

  // Once auth check completes and user is not signed in, stop rendering children.
  // Without this guard, there's a one-frame gap where children render and fire
  // API calls that 401 (e.g., ClassManagementPage fetching /classes).
  if (shouldRedirect || (!loading && !isSignedIn)) {
    return <LambLoader message="正在跳转登录..." />;
  }

  return children;
}

// Route wrapper combining auth + error boundary for student-facing pages
function ProtectedPage({ children }) {
  return (
    <OptimisticProtectedRoute>
      <SimpleErrorBoundary>
        {children}
      </SimpleErrorBoundary>
    </OptimisticProtectedRoute>
  );
}

// Kids-songs pages: no login wall — whoever lacks the course (or isn't logged in)
// gets the "activate first" page with the 公众号 QR instead (the printed song cards
// carry no code). SelinaGate lets course holders and teachers through.
function SelinaCoursePage({ children }) {
  return (
    <SimpleErrorBoundary>
      <SelinaGate>{children}</SelinaGate>
    </SimpleErrorBoundary>
  );
}

// Route wrapper combining auth + role check + error boundary for teacher pages
function TeacherRoute({ children }) {
  return (
    <OptimisticProtectedRoute>
      <RoleProtectedRoute allowedRoles={['teacher', 'admin']}>
        <SimpleErrorBoundary>
          {children}
        </SimpleErrorBoundary>
      </RoleProtectedRoute>
    </OptimisticProtectedRoute>
  );
}

// 发布中心: admins and the 'social' role (an employee who only runs posting)
function SocialRoute({ children }) {
  return (
    <OptimisticProtectedRoute>
      <RoleProtectedRoute allowedRoles={['admin', 'social']}>
        <SimpleErrorBoundary>
          {children}
        </SimpleErrorBoundary>
      </RoleProtectedRoute>
    </OptimisticProtectedRoute>
  );
}

// Route wrapper combining auth + admin role + error boundary
function AdminRoute({ children }) {
  return (
    <OptimisticProtectedRoute>
      <RoleProtectedRoute allowedRoles={['admin']}>
        <SimpleErrorBoundary>
          {children}
        </SimpleErrorBoundary>
      </RoleProtectedRoute>
    </OptimisticProtectedRoute>
  );
}

// Code splitting - lazy load all page components
const StudentDashboard = lazyWithRetry(() => import('../features/studentDashboard').then(m => ({ default: m.StudentDashboard })), 'StudentDashboard');
const QRPage = lazyWithRetry(() => import('../features/lessonViewer'), 'QRPage');
const ClassManagementPage = lazyWithRetry(() => import('../features/classManagement').then(m => ({ default: m.ClassManagementPage })), 'ClassManagementPage');
const PastExportsPage = lazyWithRetry(() => import('../features/classManagement/components/PastExportsPage'), 'PastExportsPage');
const AssignmentChatPage = lazyWithRetry(() => import('../features/assignmentChat/AssignmentChatPage'), 'AssignmentChatPage');
const BookNotesPicker = lazyWithRetry(() => import('../features/booknotes').then(m => ({ default: m.BookPicker })), 'BookNotesPicker');
const BookNotesViewer = lazyWithRetry(() => import('../features/booknotes').then(m => ({ default: m.PageViewer })), 'BookNotesViewer');
const BookNotesLivecast = lazyWithRetry(() => import('../features/booknotes').then(m => ({ default: m.LivecastViewer })), 'BookNotesLivecast');
const TestHub = lazyWithRetry(() => import('../features/tests').then(m => ({ default: m.TestHub })), 'TestHub');
const PhonicsAZTest = lazyWithRetry(() => import('../features/tests').then(m => ({ default: m.PhonicsAZTest })), 'PhonicsAZTest');
const PhonicsVowelsTest = lazyWithRetry(() => import('../features/tests').then(m => ({ default: m.PhonicsVowelsTest })), 'PhonicsVowelsTest');
const PhonicsConsonantsTest = lazyWithRetry(() => import('../features/tests').then(m => ({ default: m.PhonicsConsonantsTest })), 'PhonicsConsonantsTest');
const VocabTest = lazyWithRetry(() => import('../features/tests').then(m => ({ default: m.VocabTest })), 'VocabTest');
const MeLayout = lazyWithRetry(() => import('../features/me').then(m => ({ default: m.MeLayout })), 'MeLayout');
const MeCoursesPage = lazyWithRetry(() => import('../features/me').then(m => ({ default: m.MePage })), 'MeCoursesPage');
const MeProfilePage = lazyWithRetry(() => import('../features/me').then(m => ({ default: m.MeProfilePage })), 'MeProfilePage');
const CoursesPage = lazyWithRetry(() => import('../features/coursesPage').then(m => ({ default: m.CoursesPage })), 'CoursesPage');
const LibraryPage = lazyWithRetry(() => import('../features/library').then(m => ({ default: m.LibraryPage })), 'LibraryPage');
const LibraryProductPage = lazyWithRetry(() => import('../features/library').then(m => ({ default: m.LibraryProductPage })), 'LibraryProductPage');
const AdminDashboard = lazyWithRetry(() => import('../features/admin').then(m => ({ default: m.AdminPage })), 'AdminDashboard');
const EnglishTestPage = lazyWithRetry(() => import('../features/englishTest/EnglishTestPage'), 'EnglishTestPage');
const EnglishTestReportPage = lazyWithRetry(() => import('../features/englishTest/EnglishTestReportPage'), 'EnglishTestReportPage');
const SocialPage = lazyWithRetry(() => import('../features/social/SocialPage'), 'SocialPage');
const WecomSidebarPage = lazyWithRetry(() => import('../features/wecomSidebar/WecomSidebarPage'), 'WecomSidebarPage');
const IPAPage = lazyWithRetry(() => import('../features/ipaReference').then(m => ({ default: m.IPAPage })), 'IPAPage');
const IPACategory = lazyWithRetry(() => import('../features/ipaReference').then(m => ({ default: m.IPACategory })), 'IPACategory');
const RedeemCodePage = lazyWithRetry(() => import('../features/redemption').then(m => ({ default: m.RedeemCodePage })), 'RedeemCodePage');
const SchedulePage = lazyWithRetry(() => import('../features/schedule/SchedulePage'), 'SchedulePage');
const RoadmapLanding = lazyWithRetry(() => import('../features/roadmap').then(m => ({ default: m.RoadmapLanding })), 'RoadmapLanding');
const RoadmapPage = lazyWithRetry(() => import('../features/roadmap').then(m => ({ default: m.RoadmapPage })), 'RoadmapPage');
const CourseRoadmapPage = lazyWithRetry(() => import('../features/roadmap').then(m => ({ default: m.CourseRoadmapPage })), 'CourseRoadmapPage');
const LessonFlowPage = lazyWithRetry(() => import('../features/roadmap').then(m => ({ default: m.LessonFlowPage })), 'LessonFlowPage');
const LearningPlanFunnel = lazyWithRetry(() => import('../features/learningPlan').then(m => ({ default: m.LearningPlanFunnel })), 'LearningPlanFunnel');
const PlacementFunnel = lazyWithRetry(() => import('../features/placement').then(m => ({ default: m.PlacementFunnel })), 'PlacementFunnel');
const DailyTestPage = lazyWithRetry(() => import('../features/dailyTest').then(m => ({ default: m.DailyTestPage })), 'DailyTestPage');
const DailySpeakingPage = lazyWithRetry(() => import('../features/dailySpeaking').then(m => ({ default: m.DailySpeakingPage })), 'DailySpeakingPage');
const CategoriesRoadmapPage = lazyWithRetry(() => import('../features/categoryLevels').then(m => ({ default: m.CategoriesRoadmapPage })), 'CategoriesRoadmapPage');
const SongGalleryPage = lazyWithRetry(() => import('../features/songGallery').then(m => ({ default: m.SongGalleryPage })), 'SongGalleryPage');
const SongPlayerPage = lazyWithRetry(() => import('../features/songGallery').then(m => ({ default: m.SongPlayerPage })), 'SongPlayerPage');
const EpisodeGalleryPage = lazyWithRetry(() => import('../features/readWithSelina').then(m => ({ default: m.EpisodeGalleryPage })), 'EpisodeGalleryPage');
const EpisodePlayerPage = lazyWithRetry(() => import('../features/readWithSelina').then(m => ({ default: m.EpisodePlayerPage })), 'EpisodePlayerPage');
const RwsGate = lazyWithRetry(() => import('../features/readWithSelina').then(m => ({ default: m.RwsGate })), 'RwsGate');
const SelinaPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaPage })), 'SelinaPage');
const SelinaLearnPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaLearnPage })), 'SelinaLearnPage');
const SelinaInstallPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaInstallPage })), 'SelinaInstallPage');
const SelinaPracticePage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaPracticePage })), 'SelinaPracticePage');
const SelinaTestPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaTestPage })), 'SelinaTestPage');
const SelinaParentPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaParentPage })), 'SelinaParentPage');
const SelinaSkillQuizPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaSkillQuizPage })), 'SelinaSkillQuizPage');
const SelinaGate = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaGate })), 'SelinaGate');
const SelinaClaimPage = lazyWithRetry(() => import('../features/selina').then(m => ({ default: m.SelinaClaimPage })), 'SelinaClaimPage');

export const appRoutes = (
  <>
    {/* Home and Dashboard routes */}
    <Route path="/" element={<DashboardRedirect />} />
    {/* /go: where the static landing page at / sends people (and where an expired session lands) — same as / */}
    <Route path="/go" element={<DashboardRedirect />} />
    <Route path="/dashboard" element={<DashboardRedirect />} />
    <Route path="/logout" element={<LogoutSuccess />} />

    {/* Courses - public pages with error boundary */}
    <Route path="/courses" element={<SimpleErrorBoundary><CoursesPage /></SimpleErrorBoundary>} />
    <Route path="/courses/:courseId" element={<SimpleErrorBoundary><CoursesPage /></SimpleErrorBoundary>} />

    {/* My Library - courses the signed-in user is entitled to (auth protected) */}
    <Route path="/library" element={<ProtectedPage><LibraryPage /></ProtectedPage>} />
    <Route path="/library/explore/:sku" element={<ProtectedPage><LibraryProductPage /></ProtectedPage>} />
    <Route path="/library/:courseId" element={<ProtectedPage><LibraryPage /></ProtectedPage>} />

    {/* QR/Lesson Routes - auth protected */}
    <Route path="/qr/:courseName/:bookNumber/:lessonNumber" element={<ProtectedPage><QRPage /></ProtectedPage>} />
    <Route path="/project/:projectId" element={<ProtectedPage><QRPage /></ProtectedPage>} />

    {/* Student Dashboard */}
    <Route path="/dashboard/:studentId/*" element={<ProtectedPage><StudentDashboard /></ProtectedPage>} />

    {/* Teacher Routes */}
    <Route path="/teacher/classes" element={<TeacherRoute><ClassManagementPage /></TeacherRoute>} />
    <Route path="/teacher/classes/:classId/exports" element={<TeacherRoute><PastExportsPage /></TeacherRoute>} />
    <Route path="/teacher/chat/:classId" element={<TeacherRoute><AssignmentChatPage /></TeacherRoute>} />
    <Route path="/teacher/booknotes" element={<TeacherRoute><BookNotesPicker /></TeacherRoute>} />
    <Route path="/teacher/booknotes/:bookId/livecast" element={<TeacherRoute><BookNotesLivecast /></TeacherRoute>} />
    <Route path="/teacher/booknotes/:bookId" element={<TeacherRoute><BookNotesViewer /></TeacherRoute>} />

    {/* Admin Routes */}
    <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
    <Route path="/social" element={<SocialRoute><SocialPage /></SocialRoute>} />

    {/* 企业微信 chat sidebar — opened inside WeCom beside a parent's chat; WeCom's own OAuth
        proves the viewer is our staff (no app login there). */}
    <Route path="/wecom/sidebar" element={<SimpleErrorBoundary><WecomSidebarPage /></SimpleErrorBoundary>} />

    {/* IPA Chart - public */}
    <Route path="/ipa" element={<SimpleErrorBoundary><IPAPage /></SimpleErrorBoundary>} />
    <Route path="/ipa/:category" element={<SimpleErrorBoundary><IPACategory /></SimpleErrorBoundary>} />

    {/* Test Routes - public */}
    <Route path="/test" element={<SimpleErrorBoundary><TestHub /></SimpleErrorBoundary>} />
    <Route path="/test/phonics-az" element={<SimpleErrorBoundary><PhonicsAZTest /></SimpleErrorBoundary>} />
    <Route path="/test/phonics-vowels" element={<SimpleErrorBoundary><PhonicsVowelsTest /></SimpleErrorBoundary>} />
    <Route path="/test/phonics-consonants" element={<SimpleErrorBoundary><PhonicsConsonantsTest /></SimpleErrorBoundary>} />
    <Route path="/test/vocab" element={<SimpleErrorBoundary><VocabTest /></SimpleErrorBoundary>} />
    {/* 免费英语测评 — public (a lead, no login); graded by a teacher in the 企业微信 sidebar */}
    <Route path="/test/english" element={<SimpleErrorBoundary><EnglishTestPage /></SimpleErrorBoundary>} />
    {/* The short link a teacher sends one parent from the sidebar */}
    <Route path="/t/:code" element={<SimpleErrorBoundary><EnglishTestPage /></SimpleErrorBoundary>} />
    <Route path="/test/english/report/:token" element={<SimpleErrorBoundary><EnglishTestReportPage /></SimpleErrorBoundary>} />

    {/* Auth Routes */}
    <Route path="/auth/callback" element={<AuthCallback />} />
    <Route path="/paywall" element={<Paywall />} />

    {/* My Products - student's purchased courses */}
    <Route path="/me" element={<ProtectedPage><MeLayout /></ProtectedPage>}>
      <Route index element={<MeCoursesPage />} />
      <Route path="profile" element={<MeProfilePage />} />
    </Route>

    {/* Roadmap - public, lazy-loaded */}
    <Route path="/app" element={<SimpleErrorBoundary><RoadmapLanding /></SimpleErrorBoundary>} />
    <Route path="/app/:productSlug" element={<SimpleErrorBoundary><RoadmapPage /></SimpleErrorBoundary>} />
    <Route path="/app/:productSlug/:courseId" element={<SimpleErrorBoundary><CourseRoadmapPage /></SimpleErrorBoundary>} />

    {/* Lesson flow: video → quiz → done */}
    <Route path="/lesson/:courseId/:bookId/:lessonNumber" element={<ProtectedPage><LessonFlowPage /></ProtectedPage>} />

    {/* Song Gallery — kid-mode browse + simplified player */}
    <Route path="/songs/gallery" element={<ProtectedPage><SongGalleryPage /></ProtectedPage>} />
    <Route path="/songs/gallery/:seq" element={<ProtectedPage><SongPlayerPage /></ProtectedPage>} />
    {/* Owners watch everything; anyone else previews one episode (access/rwsAccess.jsx). */}
    <Route path="/read-with-selina" element={<ProtectedPage><RwsGate><EpisodeGalleryPage /></RwsGate></ProtectedPage>} />
    <Route path="/read-with-selina/:seq" element={<ProtectedPage><RwsGate><EpisodePlayerPage /></RwsGate></ProtectedPage>} />

    {/* Selina英语启蒙 (kids songs) — home is every song numbered like the printed song cards, with category
        pills; a song opens its page (video + lyrics) with 学 (picture words) and 测 (picture quiz) for its category.
        The weekly plan was retired 2026-09-25. /learn is kept for old links. */}
    <Route path="/kidsSongs" element={<SelinaCoursePage><SelinaPage /></SelinaCoursePage>} />
    <Route path="/kidsSongs/learn" element={<SelinaCoursePage><SelinaLearnPage /></SelinaCoursePage>} />
    {/* The song page; each printed card's QR opens /kidsSongs/all?song=N */}
    <Route path="/kidsSongs/all" element={<SelinaCoursePage><SelinaLearnPage /></SelinaCoursePage>} />
    {/* 添加到桌面 guide — public: the WeCom welcome links here, often before the parent has logged in */}
    <Route path="/kidsSongs/add" element={<SimpleErrorBoundary><SelinaInstallPage /></SimpleErrorBoundary>} />
    <Route path="/kidsSongs/practice" element={<SelinaCoursePage><SelinaPracticePage /></SelinaCoursePage>} />
    <Route path="/kidsSongs/test" element={<SelinaCoursePage><SelinaTestPage /></SelinaCoursePage>} />
    {/* 家长看: the words a child understands, by level (Rob, 2026-10-16) */}
    <Route path="/kidsSongs/parent" element={<SelinaCoursePage><SelinaParentPage /></SelinaCoursePage>} />
    {/* 说 / 读 / 写 — a parent quizzes their child on a topic, from 家长看 (Rob, 2026-10-17) */}
    <Route path="/kidsSongs/skill" element={<SelinaCoursePage><SelinaSkillQuizPage /></SelinaCoursePage>} />
    {/* One-tap claim link on shop deliveries (Douyin cards print /kidsSongs/claim, WeChat sends /claim) — public: shows the product BEFORE login */}
    <Route path="/claim" element={<SimpleErrorBoundary><SelinaClaimPage /></SimpleErrorBoundary>} />
    {/* 每日数据上传 — Rob's assistant uploads yesterday's kids 视频号 CSV; public, the ?k= key in the link is the access */}
    <Route path="/kidsSongs/claim" element={<SimpleErrorBoundary><SelinaClaimPage /></SimpleErrorBoundary>} />

    {/* Daily test — SRS review of previously failed items (server-gated rollout) */}
    <Route path="/daily-test" element={<ProtectedPage><DailyTestPage /></ProtectedPage>} />
    <Route path="/daily-test/:studentId" element={<ProtectedPage><DailyTestPage /></ProtectedPage>} />
    <Route path="/daily-speaking/:studentId" element={<ProtectedPage><DailySpeakingPage /></ProtectedPage>} />

    {/* Category levels — Family/Activities/Countries circles page (server-gated rollout) */}
    <Route path="/categories" element={<ProtectedPage><CategoriesRoadmapPage /></ProtectedPage>} />
    <Route path="/categories/:studentId" element={<ProtectedPage><CategoriesRoadmapPage /></ProtectedPage>} />

    {/* Schedule - course progress chart */}
    <Route path="/schedule" element={<SchedulePage />} />

    {/* Personalized learning-plan funnel — public lead-gen. /free is the marketing slug; /roadmap is an alias. */}
    <Route path="/free" element={<SimpleErrorBoundary><LearningPlanFunnel /></SimpleErrorBoundary>} />
    <Route path="/roadmap" element={<SimpleErrorBoundary><LearningPlanFunnel /></SimpleErrorBoundary>} />

    {/* Placement / level test — public anonymous funnel. */}
    <Route path="/placement" element={<SimpleErrorBoundary><PlacementFunnel /></SimpleErrorBoundary>} />
    {/* Authenticated retest — ties the result to a student + shows progress. */}
    <Route path="/placement/retest/:studentId" element={<ProtectedPage><PlacementFunnel /></ProtectedPage>} />

    {/* Redemption */}
    <Route path="/redeem" element={<SimpleErrorBoundary><RedeemCodePage /></SimpleErrorBoundary>} />

    {/* 404 */}
    <Route path="*" element={<NotFound />} />
  </>
);
